import { useEffect, useState } from "react";
import { API_BASE, ApiRequestError, getMcpOrgs, selectMcpOrg, type McpOrg } from "../lib/api.ts";
import { Select } from "../components/Select.tsx";

/**
 * MCP consent / org-picker page for the better-auth mcp() OAuth flow.
 *
 * better-auth's /mcp/authorize redirects here (loginPage) with the OAuth client
 * + the original authorize params (verified live: response_type, client_id,
 * redirect_uri, scope, code_challenge, code_challenge_method, state — NO
 * consent_code at the login step). The user picks the org these MCP calls count
 * against; we bind it (mcp_grant_org), then continue the OAuth flow by
 * re-submitting to /mcp/authorize (now with the session) — which issues the code
 * and redirects back to the MCP client. If better-auth inserts a separate consent
 * step (a `consent_code`), we complete that instead.
 */

// Params we echo back to /mcp/authorize to resume the flow after binding the org.
const AUTHORIZE_PARAMS = [
  "response_type", "client_id", "redirect_uri", "scope",
  "code_challenge", "code_challenge_method", "state", "nonce", "resource", "prompt",
];

export function ConnectMcpPage() {
  const qs = new URLSearchParams(window.location.search);
  const consentCode = qs.get("consent_code") ?? qs.get("consentCode") ?? "";

  const [orgs, setOrgs] = useState<McpOrg[] | null>(null);
  const [orgId, setOrgId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);

  useEffect(() => {
    getMcpOrgs()
      .then((r) => {
        setOrgs(r.orgs);
        setOrgId(r.orgs[0]?.id ?? "");
      })
      .catch((e) => {
        if (e instanceof ApiRequestError && e.status === 401) {
          const next = encodeURIComponent(window.location.pathname + window.location.search);
          window.location.href = `/login?next=${next}`;
          return;
        }
        setError("Couldn't load your organizations.");
      })
      .finally(() => setLoading(false));
  }, []);

  function authorizeUrl(): string {
    const u = new URL(`${API_BASE}/api/auth/mcp/authorize`);
    for (const k of AUTHORIZE_PARAMS) {
      const v = qs.get(k);
      if (v) u.searchParams.set(k, v);
    }
    return u.toString();
  }

  const isConsentStep = !!consentCode;
  const isLoginStep = !!qs.get("response_type");

  async function approve() {
    if (!orgId) return;
    if (!isConsentStep && !isLoginStep) {
      setError("Open this page from your MCP client's connect flow, not directly.");
      return;
    }
    setWorking(true);
    setError(null);
    try {
      // Record the chosen org for this connection (pinned to the client by /v1).
      await selectMcpOrg(orgId);

      if (consentCode) {
        // Separate consent step — complete better-auth's oidc-provider consent
        // (endpoint is /api/auth/oauth2/consent, NOT under /mcp).
        const res = await fetch(`${API_BASE}/api/auth/oauth2/consent`, {
          method: "POST",
          credentials: "include",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ accept: true, consent_code: consentCode }),
        });
        const data = (await res.json().catch(() => ({}))) as {
          redirectURI?: string; redirect?: string; url?: string; redirect_uri?: string;
        };
        const redirect =
          data.redirectURI ?? data.redirect ?? data.redirect_uri ?? data.url ?? (res.redirected ? res.url : "");
        if (redirect) window.location.href = redirect;
        else {
          setError(`Consent returned ${res.status} with no redirect: ${JSON.stringify(data).slice(0, 160)}`);
          setWorking(false);
        }
      } else {
        // Login step — resume the OAuth flow (now with the session) so better-auth
        // issues the code and redirects back to the MCP client.
        window.location.href = authorizeUrl();
      }
    } catch {
      setError("Couldn't complete the connection.");
      setWorking(false);
    }
  }

  return (
    <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", padding: "24px" }}>
      <div className="card card-pad" style={{ width: "100%", maxWidth: "440px" }}>
        <h1 style={{ fontSize: "20px", margin: "0 0 6px" }}>Connect to CurrencyCore</h1>
        <p className="muted" style={{ marginTop: 0 }}>
          An MCP client wants to use the CurrencyCore APIs on your behalf. Choose the organization whose
          plan + usage these requests should count against.
        </p>

        {loading && <p className="muted">Loading…</p>}
        {!loading && (
          <p className="help" style={{ fontSize: "11px", opacity: 0.7 }}>
            {isConsentStep ? "consent step" : isLoginStep ? "sign-in step" : "opened directly"}
            {` · params: ${[...qs.keys()].join(", ") || "none"}`}
          </p>
        )}
        {error && <div className="error-banner" style={{ margin: "12px 0" }}>{error}</div>}

        {orgs && !loading && (
          orgs.length === 0 ? (
            <p className="muted">You don't belong to any organization yet. Create one first, then reconnect.</p>
          ) : (
            <>
              <div className="field" style={{ margin: "16px 0" }}>
                <label>Organization</label>
                <Select
                  value={orgId}
                  ariaLabel="Organization"
                  options={orgs.map((o) => ({ value: o.id, label: o.name }))}
                  onChange={setOrgId}
                />
                <span className="help">
                  MCP requests are rate-limited and billed like any API key on this org. You can disable
                  MCP any time from the dashboard.
                </span>
              </div>
              <div style={{ display: "flex", gap: "10px", marginTop: "8px" }}>
                <button className="btn btn-primary" disabled={working || !orgId} onClick={() => void approve()}>
                  {working ? <><span className="spinner" />Connecting…</> : "Approve & connect"}
                </button>
                <button className="btn btn-ghost" disabled={working} onClick={() => window.history.back()}>
                  Cancel
                </button>
              </div>
            </>
          )
        )}
      </div>
    </div>
  );
}
